/**
 * Timezone utility for Eastern Time (ET) conversion
 * Handles both EST (UTC-5) and EDT (UTC-4) automatically
 */

/**
 * Get current date/time in Eastern Time Zone
 * @returns {Date} Date object adjusted to Eastern Time
 */
export const getEasternTime = () => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
};

/**
 * Convert any date to Eastern Time
 * @param {Date|string} date - Date to convert
 * @returns {Date} Date object in Eastern Time
 */
export const toEasternTime = (date) => {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Date(dateObj.toLocaleString('en-US', { timeZone: 'America/New_York' }));
};

/**
 * Get current hour in Eastern Time (0-23)
 * @returns {number} Current hour in ET
 */
export const getEasternHour = () => {
  return getEasternTime()?.getHours();
};

/**
 * Format date to locale string in Eastern Time
 * @param {Date|string} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatEasternDate = (date, options = {}) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj?.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    ...options
  });
};

/**
 * Format date to locale date string in Eastern Time
 * @param {Date|string} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatEasternDateOnly = (date, options = {}) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj?.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    ...options
  });
};

/**
 * Get greeting based on Eastern Time
 * @returns {string} Time-appropriate greeting
 */
export const getEasternGreeting = () => {
  const hour = getEasternHour();
  if (hour < 12) return 'Good Morning';
  if (hour < 18) return 'Good Afternoon';
  return 'Good Evening';
};